import { useEffect, useState } from "react";
import SelectMultipleGenericFilter from "../SelectMultipleGenericFilter";
import { GenericSkuFilterTypeId, SkuAvailabilityId } from "types/commonTypes";
import SelectGenericFilter from "../SelectGenericFilter";
import SelectDayOfWeek from "../SelectDayOfWeek";
import EventStartDatePicker from "../EventStartDatePicker";
import EventEndDatePicker from "../EventEndDatePicker";
import EventStartTimePicker from "../EventStartTimePicker";
import AttributesFilter from "../AttributesFilter";
import { CatalogFilter } from "services/organizationPortalClient";
import { CommonFilter, TableViewType, useIsFilterApplied } from "@knowfully/ui.common";

type LiveOnlineCatalogFilterProps = {
  view: TableViewType;
  onChange: (filter: CatalogFilter) => void;
};

export default function LiveOnlineCatalogFilter({
  view,
  onChange,
}: LiveOnlineCatalogFilterProps) {
  const [filter, setFilter] = useState({} as CatalogFilter);

  const isFilterApplied = useIsFilterApplied(filter, []);

  useEffect(() => {
    if (view === "calendar") {
      onChangeInternal("eventStartDate", null);
      onChangeInternal("eventEndDate", null);
    }
  }, [view]);

  const onChangeInternal = (prop: keyof CatalogFilter, val: any) => {
    setFilter((prev) => ({ ...prev, [prop]: val }) as CatalogFilter);
  };

  // Auto-apply filters to parent on any change
  useEffect(() => {
    onChange(filter);
  }, [filter, onChange]);


  const clearFilter = () => {
    const emptyFilter = {} as CatalogFilter;
    setFilter(emptyFilter);
  };

  return (
    <CommonFilter
      searchText={filter.searchText}
      onSearchTextChange={(val: string | undefined) => onChangeInternal("searchText", val)}
      onClear={clearFilter}
      isFilterApplied={isFilterApplied}
    >
      <SelectGenericFilter
        labelKey="creditHours"
        typeId={GenericSkuFilterTypeId.CreditHours}
        skuAvailabilityIds={[SkuAvailabilityId.Live]}
        gridSize={4}
        value={filter.creditHours}
        onChange={(value) => onChangeInternal("creditHours", value)}
      />
      {view === "list" && (
        <>
          <EventStartDatePicker
            value={filter.eventStartDate}
            onChange={(value) => onChangeInternal("eventStartDate", value)}
          />
          <EventEndDatePicker
            value={filter.eventEndDate}
            onChange={(value) => onChangeInternal("eventEndDate", value)}
          />
          <SelectDayOfWeek
            value={filter.dayOfWeek}
            onChange={(value) => onChangeInternal("dayOfWeek", value)}
          />
        </>
      )}
      {/* Stays outside the list-view guard: the time filter applies to the calendar too. */}
      <EventStartTimePicker
        value={filter.startTime}
        onChange={(value) => onChangeInternal("startTime", value)}
      />
      <SelectGenericFilter
        labelKey="topic"
        typeId={GenericSkuFilterTypeId.Topic}
        skuAvailabilityIds={[SkuAvailabilityId.Live]}
        gridSize={4}
        value={filter.topicId}
        onChange={(value) => onChangeInternal("topicId", value)}
      />
      <SelectGenericFilter
        labelKey="instructor"
        typeId={GenericSkuFilterTypeId.Resource}
        skuAvailabilityIds={[SkuAvailabilityId.Live]}
        gridSize={4}
        value={filter.instructorId}
        onChange={(value) => onChangeInternal("instructorId", value)}
      />
      <SelectMultipleGenericFilter
        labelKey="professions"
        typeId={GenericSkuFilterTypeId.Profession}
        skuAvailabilityIds={[SkuAvailabilityId.Live]}
        value={filter.professionIds}
        onChange={(value) => onChangeInternal("professionIds", value)}
      />
      <AttributesFilter
        skuAvailabilityIds={[SkuAvailabilityId.Live]}
        value={filter.attributesFilters ?? []}
        onChange={(value) => onChangeInternal("attributesFilters", value)}
      />
    </CommonFilter>
  );
}
